import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Zap, CreditCard, Shield, Check } from 'lucide-react';
type PlanId = 'base' | 'completo' | 'pro';
export function PricingSection() {
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('completo');
  const plans = [{
    id: 'base' as PlanId,
    icon: '📘',
    name: 'PLAN BASE',
    description: '4 tomos + 1 bono',
    oldPrice: '$49.990',
    price: '$19.990',
    highlight: false,
    checkoutUrl: 'https://checkout.rincondigital.shop/cart/54007461708051:1',
    contents: ['TOMO 1: Fundamentos del Polo', 'TOMO 2: Técnica Ecuestre', 'TOMO 3: Táctica y Estrategia', 'TOMO 4: Entrenamiento Físico', 'BONO: Plan Maestro 12 Meses']
  }, {
    id: 'completo' as PlanId,
    icon: '🏆',
    name: 'PACK COMPLETO',
    description: '5 tomos + 3 bonos',
    oldPrice: '$69.990',
    price: '$24.990',
    highlight: true,
    badge: 'MÁS ELEGIDO',
    savings: 'AHORRÁS $45.000',
    checkoutUrl: 'https://checkout.rincondigital.shop/cart/54007462134035:1',
    contents: ['TOMO 1: Fundamentos del Polo', 'TOMO 2: Técnica Ecuestre', 'TOMO 3: Táctica y Estrategia', 'TOMO 4: Entrenamiento Físico', 'TOMO 5: Evaluación y Competición', 'BONO 1: Plan Maestro 12 Meses', 'BONO 2: Kit de Evaluación PRO', 'BONO 3: Guiones de Clase "Cero Dudas"']
  }, {
    id: 'pro' as PlanId,
    icon: '⭐',
    name: 'ACADEMIA PRO',
    description: '5 tomos + 5 bonos + Sistema Academia PRO',
    oldPrice: '$79.990',
    price: '$29.990',
    highlight: false,
    checkoutUrl: 'https://checkout.rincondigital.shop/cart/54007462297875:1',
    contents: ['TOMO 1: Fundamentos del Polo', 'TOMO 2: Técnica Ecuestre', 'TOMO 3: Táctica y Estrategia', 'TOMO 4: Entrenamiento Físico', 'TOMO 5: Evaluación y Competición', 'BONO 1: Plan Maestro 12 Meses', 'BONO 2: Kit de Evaluación PRO', 'BONO 3: Guiones de Clase "Cero Dudas"', 'BONO 4: Biblioteca Express de Drills', 'BONO 5: Kit de Gestión Docente', 'Sistema "Tu Propia Academia de Polo"']
  }];
  const selectedPlanData = plans.find(p => p.id === selectedPlan);
  const getButtonText = () => {
    switch (selectedPlan) {
      case 'base':
        return 'SÍ, QUIERO PLAN BASE →';
      case 'completo':
        return 'SÍ, QUIERO PACK COMPLETO →';
      case 'pro':
        return 'SÍ, QUIERO ACADEMIA PRO →';
      default:
        return 'SÍ, QUIERO PACK COMPLETO →';
    }
  };
  return <section id="pricing" className="py-8 md:py-24 bg-polo-navyLight relative" data-id="element-100">
      <div className="max-w-3xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-101">
        <div className="text-center mb-5 md:mb-12" data-id="element-102">
          <h2 className="text-2xl md:text-5xl font-bold mb-1.5 md:mb-4" data-id="element-103">
            Elegí tu plan
          </h2>
          <p className="text-sm md:text-xl text-polo-goldLight" data-id="element-104">
            Dejá de perder tiempo planificando. Volvé a disfrutar la cancha.
          </p>
        </div>

        <div className="space-y-2.5 md:space-y-4 mb-5 md:mb-8" data-id="element-105">
          {plans.map(plan => {
          const isSelected = selectedPlan === plan.id;
          return <motion.div key={plan.id} whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} onClick={() => setSelectedPlan(plan.id)} className={`relative cursor-pointer rounded-xl md:rounded-2xl p-3 md:p-6 transition-all duration-200 flex items-center gap-3 md:gap-6 ${plan.highlight ? isSelected ? 'bg-black/40 border-2 border-polo-gold shadow-[0_0_20px_rgba(201,168,76,0.15)]' : 'bg-black/40 border-2 border-polo-gold/50' : isSelected ? 'bg-black/20 border-2 border-white/20' : 'bg-black/20 border border-white/5 hover:border-white/10'}`} data-id="element-106">
                {/* Radio Button */}
                <div className="shrink-0" data-id="element-107">
                  <div className={`w-5 h-5 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-polo-gold' : 'border-gray-500'}`} data-id="element-108">
                    {isSelected && <div className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 rounded-full bg-polo-gold" data-id="element-109" />}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow flex items-center justify-between gap-2 min-w-0" data-id="element-110">
                  <div className="min-w-0" data-id="element-111">
                    <div className="flex items-center gap-1.5 md:gap-2" data-id="element-112">
                      <span className="text-base md:text-xl" data-id="element-113">{plan.icon}</span>
                      <h3 className="text-sm md:text-xl font-bold text-white uppercase tracking-wide truncate" data-id="element-114">
                        {plan.name}
                      </h3>
                    </div>

                    {plan.highlight && <div className="flex flex-wrap gap-1 md:gap-2 mt-1 mb-0.5 md:mb-2" data-id="element-115">
                        <span className="inline-flex items-center gap-0.5 bg-polo-gold text-polo-navy text-[8px] md:text-xs font-bold px-1.5 py-px md:px-2 md:py-0.5 rounded-sm uppercase leading-tight" data-id="element-116">
                          ⭐ {plan.badge}
                        </span>
                        <span className="inline-block bg-polo-gold/20 text-polo-gold border border-polo-gold/30 text-[8px] md:text-xs font-bold px-1.5 py-px md:px-2 md:py-0.5 rounded-sm uppercase leading-tight" data-id="element-117">
                          {plan.savings}
                        </span>
                      </div>}

                    <p className="text-[11px] md:text-base text-gray-400 truncate" data-id="element-118">
                      {plan.description}
                    </p>
                  </div>

                  <div className="text-right shrink-0" data-id="element-119">
                    <div className="text-[10px] md:text-base text-gray-500 line-through leading-tight" data-id="element-120">
                      {plan.oldPrice}
                    </div>
                    <div className={`text-xl md:text-4xl font-bold leading-tight ${plan.highlight ? 'text-polo-gold' : 'text-white'}`} data-id="element-121">
                      {plan.price}
                    </div>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        <div className="text-center" data-id="element-122">
          <a href={selectedPlanData?.checkoutUrl || '#'} className="block w-full bg-polo-gold hover:bg-polo-goldHover text-polo-navy font-black text-base md:text-2xl py-4 md:py-6 px-4 md:px-6 rounded-xl transition-all shadow-[0_0_30px_rgba(201,168,76,0.3)] hover:scale-105 active:scale-95 mb-2 md:mb-4" data-id="element-123">
            {getButtonText()}
          </a>
          <p className="text-xs md:text-base text-gray-400 italic mb-4 md:mb-6" data-id="element-124">
            La opción más elegida por docentes que enseñan en los dos niveles
          </p>
        </div>

        {/* Selected plan contents */}
        <AnimatePresence mode="wait" data-id="element-125">
          {selectedPlanData && <motion.div key={selectedPlan} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="glass-card rounded-xl p-4 md:p-6 border-white/10 mb-5 md:mb-8" data-id="element-126">
              <p className="text-xs md:text-sm font-bold text-polo-gold uppercase tracking-wider mb-3" data-id="element-127">
                {selectedPlanData.icon} {selectedPlanData.name} incluye:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 md:gap-y-2" data-id="element-128">
                {selectedPlanData.contents.map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-129">
                    <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-400 shrink-0 mt-0.5" data-id="element-130" />
                    <span className="text-xs md:text-sm text-gray-300" data-id="element-131">
                      {item}
                    </span>
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        <div className="glass-card rounded-lg md:rounded-xl p-3 md:p-4 border-white/5 flex flex-wrap justify-center gap-3 md:gap-8 text-[10px] md:text-sm text-gray-300" data-id="element-132">
          <div className="flex items-center gap-1" data-id="element-133">
            <Shield className="w-3 h-3 md:w-4 md:h-4 text-polo-gold" data-id="element-134" /> Pago
            seguro
          </div>
          <div className="flex items-center gap-1" data-id="element-135">
            <Zap className="w-3 h-3 md:w-4 md:h-4 text-polo-gold" data-id="element-136" /> Acceso en 5
            min
          </div>
          <div className="flex items-center gap-1" data-id="element-137">
            <ShieldCheck className="w-3 h-3 md:w-4 md:h-4 text-polo-gold" data-id="element-138" />{' '}
            Garantía 7 días
          </div>
          <div className="flex items-center gap-1" data-id="element-139">
            <CreditCard className="w-3 h-3 md:w-4 md:h-4 text-polo-gold" data-id="element-140" />{' '}
            Mercado Pago
          </div>
        </div>
      </div>
    </section>;
}